import {
  DataLayerContext,
  SummaryStatsType,
} from 'features/dataLayers/DataLayerProvider'
import { useCdfDataFetcher } from 'features/dataLayers/load/useCdfDataFetcher'
import memoize from 'lodash/memoize'
import { useCallback, useContext } from 'react'
import { GeckoComponentDataFrame } from 'types'

import { CategoricalSummaryStatistics } from './categoricalStatisticsDataType'
import { generateCategoricalDataStatsInWorker } from './generateCategoricalDataStatsInWorker'

/**
 * A hook that returns a function which, when called, fetches the categorical summary statistics for a given CDF.
 */
export function useCategoricalDataSummaryStatsFetcher(): (
  cdf: GeckoComponentDataFrame
) => Promise<CategoricalSummaryStatistics | undefined> {
  const { cdfDataStatsFetchCache } = useContext(DataLayerContext)
  const cdfDataFetcher = useCdfDataFetcher()
  return useCallback(
    async (
      cdf: GeckoComponentDataFrame
    ): Promise<CategoricalSummaryStatistics | undefined> => {
      const cachedValues = cdfDataStatsFetchCache.get(cdf.id)
      if (
        cachedValues &&
        cachedValues.generation === cdf.generation &&
        cachedValues.type === SummaryStatsType.Categorical
      ) {
        return await cachedValues.getData()
      } else if (
        cachedValues?.generation !== cdf.generation ||
        cachedValues.type !== SummaryStatsType.Categorical
      ) {
        cdfDataStatsFetchCache.delete(cdf.id)
      }

      function _getCDFDataFunc(): Promise<
        CategoricalSummaryStatistics | undefined
      > {
        return new Promise((resolve) => {
          cdfDataFetcher(cdf).then((data) => {
            if (data === undefined) {
              resolve(undefined)
            } else {
              generateCategoricalDataStatsInWorker(data).then(resolve)
            }
          })
        })
      }

      const memoizedGetterFn = memoize(_getCDFDataFunc)

      cdfDataStatsFetchCache.set(cdf.id, {
        generation: cdf.generation,
        getData: memoizedGetterFn,
        type: SummaryStatsType.Categorical,
      })

      return await memoizedGetterFn()
    },
    [cdfDataFetcher, cdfDataStatsFetchCache]
  )
}
